/**
 * MSAL (Microsoft Authentication Library) initialization.
 *
 * Called once at app startup from index.tsx, before React renders.
 * Returns a PublicClientApplication instance that gets passed to
 * <MsalProvider> to enable Azure AD authentication across the app.
 *
 * ## Environment handling and VITE_LOCAL_OVERRIDE_ENVIRONMENT
 *
 * When VITE_LOCAL_OVERRIDE_ENVIRONMENT is set (e.g., "production"), the app
 * points API calls at a remote server but runs from localhost. MSAL uses the
 * OVERRIDE target's Azure app registration — every Hubble app registration
 * (preprod/demo/production) has the localhost:5x73/auth-redirect.html ports
 * pre-registered as SPA redirect URIs, so the override target's clientId
 * works directly. Using the matching clientId is required for endpoints that
 * do a Microsoft Graph on-behalf-of exchange (e.g. sales-email send), since
 * Graph rejects OBO assertions whose `aud` doesn't match the backend's app
 * id (AADSTS500131).
 *
 * ## Auth disabled mode
 *
 * When VITE_DISABLE_AUTHENTICATION=true (local dev / preview / e2e), this
 * returns undefined and no MsalProvider wraps the app. The current user
 * comes from the signed `amy-preview-access` cookie (see useCurrentUser in
 * @freightsimple/hubble-api), falling back to a built-in dev identity.
 *
 * ## MSAL 5.x
 *
 * Requires `await instance.initialize()` before any account operations.
 * This is an async call that contacts Azure AD — it will timeout if
 * the Azure app registration is misconfigured or network is unavailable.
 */

import {
  PublicClientApplication,
  EventType,
  type AuthenticationResult,
  type EventMessage,
} from "@azure/msal-browser";
import { getMsalConfig } from "@freightsimple/hubble-api/authConfig";
import { setUserContext } from "@freightsimple/observability/errorReporting";
import {
  mark,
  startTimedSpan,
} from "@freightsimple/observability/observability";
import { environment, isAuthenticationDisabled } from "../../environment";

export async function initAuth(): Promise<PublicClientApplication | undefined> {
  if (isAuthenticationDisabled()) {
    return undefined;
  }

  const initSpan = startTimedSpan("auth.init", { environment });

  try {
    const config = getMsalConfig(environment);
    const instance = new PublicClientApplication(config);

    // MSAL 5.x requires async initialization before any account operations.
    // This is the network call to Azure AD we suspect contributes to cold-load.
    const initializeSpan = startTimedSpan("auth.msal_initialize");
    try {
      await instance.initialize();
      initializeSpan.end();
    } catch (e) {
      initializeSpan.recordError(e);
      initializeSpan.end({ error: true });
      throw e;
    }

    // Restore the active account from the previous session (stored in localStorage)
    const accounts = instance.getAllAccounts();
    const hadCachedAccount = accounts.length > 0;
    if (hadCachedAccount && accounts[0]) {
      instance.setActiveAccount(accounts[0]);
      setUserContext(accounts[0].homeAccountId, accounts[0].username);
    }

    // Set the active account after a successful login redirect
    instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        if (payload.account) {
          instance.setActiveAccount(payload.account);
          setUserContext(
            payload.account.homeAccountId,
            payload.account.username,
          );
        }
      }
    });

    initSpan.end({
      had_cached_account: hadCachedAccount,
      account_count: accounts.length,
    });
    return instance;
  } catch (e) {
    initSpan.recordError(e);
    initSpan.end({ error: true });
    mark("auth.init.error", {
      name: e instanceof Error ? e.name : "unknown",
    });
    throw e;
  }
}
